import { Accordion, AccordionItem } from "@heroui/accordion";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router";
import { GetLocalStorage } from "../../../../../utils/functions/GetLocalStorage.js";
import { FormatDateTime } from "../../../../../utils/functions/ResourceFunctions.js";
import {
  useCalculateExpectedScoreMutation,
  useCalculateKraScoreMutation,
  useCalculateMainScoreMutation,
  useCreateLineManagerReviewSummaryMutation,
  useGetAppraisalReviewDetailsQuery,
  useGetMainSelfReviewByAppraisalTypeQuery,
  useSaveLineManagerReviewMutation,
} from "../../../../api/employeeApi";
import DashboardStyle from "../../../../dashboard/Styles/Dashboard.module.css";
import { ActionButtons } from "../../../../global/components/Buttons/buttons";
import bull from "../../../assets/bull.png";
import flag from "../../../assets/finish-FLAG.png";
import person from "../../../assets/personAppraisal.png";
import NeutralDocumentUploadComponent from "../../../Components/DocumentUploader/index.jsx";
import {
  FormInput,
  FormTextArea,
} from "../../../Components/Forms/InputTemplate";
import PageLayout from "../../../Components/Layout/PageLayout.js";
import { MetaData } from "../SelfAppraisal/data.jsx";
import AppraisalReviewDetails from "../SelfAppraisal/reviewDetails.jsx";

const LineManagerView = () => {
  const user = GetLocalStorage();
  const { id, referenceId } = useParams();
  const navigation = useNavigate();
  const { data, refetch } = useGetMainSelfReviewByAppraisalTypeQuery({
    referenceId,
    appraisalType: 1,
    appraiserId: user.staffId,
  });

  const [onSubmit, { isLoading }] = useCreateLineManagerReviewSummaryMutation();
  const { data: details } = useGetAppraisalReviewDetailsQuery(id);
  const allKras = useMemo(() => {
    return data?.responseObject?.objectives?.flatMap((obj) => obj.krAs) ?? [];
  }, [data]);
  const employeeData = data?.responseObject?.employee;

  const [kraId, setKraId] = useState(0);
  const [generalDocuments, setGeneralDocuments] = useState([]);
  const formMethods = useForm({
    mode: "all",
    defaultValues: {},
  });

  const { handleSubmit, reset } = formMethods;
  const kras = details?.responseObject?.groupedObjectives;

  const [onCalculate] = useCalculateKraScoreMutation();
  const [expectedScore] = useCalculateExpectedScoreMutation();
  const watched = useWatch({ control: formMethods.control });
  const initialized = React.useRef(false);
  const debounceRef = React.useRef(null);
  const debounceRefMain = React.useRef(null);
  const debounceExpected = React.useRef(null);
  const [onCalculateMainScore] = useCalculateMainScoreMutation();
  const [score, setScore] = useState({
    actualValue: 0,
    targetValue: 0,
    weight: 0,
  });
  const [kraPerformanceScores, setKraPerformanceScores] = useState({});
  const [totalScore, setTotalScore] = useState({
    colorType: "",
    totalAppraisalScore: 0,
    performanceStatusLabelName: "",
  });
  const [maxScore, setMaxScore] = useState(0);
  const [item, setItem] = useState();
  const [KraIndex, setKraIndex] = useState();
  const [objIndex, setObjIndex] = useState();
  const currentActualValue =
    objIndex !== undefined && KraIndex !== undefined
      ? watched[`KRAs_${objIndex}_${KraIndex}_actualScore`]
      : 0;

  useEffect(() => {
    if (!score.actualValue && !score.targetValue && !score.weight) return;
    if (!item?.kraId) return;

    if (debounceRefMain.current) clearTimeout(debounceRefMain.current);

    debounceRefMain.current = setTimeout(async () => {
      try {
        const res = await onCalculateMainScore({
          actualValue: score.actualValue,
          targetValue: score.targetValue,
          weight: score.weight,
        }).unwrap();

        if (res) {
          setKraPerformanceScores((prev) => ({
            ...prev,
            [item.kraId]: res.kraPerformanceScore,
          }));
        }
      } catch (err) {
        console.error("Error calculating main score:", err);
      }
    }, 500);

    return () => {
      if (debounceRefMain.current) clearTimeout(debounceRefMain.current);
    };
  }, [score, onCalculateMainScore, item?.kraId]);

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      return;
    }

    if (!watched || !allObjectives.length) return; // Early return if no data

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(async () => {
      try {
        const objectives = allObjectives.map((objective, objIndex) => {
          const krAs = objective.krAs.map((kra, kraIndex) => {
            const kraKey = `${objIndex}_${kraIndex}`;
            const actualScore =
              watched[`KRAs_${kraKey}_actualScore`] ||
              kra?.employeeReviewDetails?.find(
                (item) => item.appraisalType === 1
              )?.kraActualScore ||
              0;
            return {
              kraId: kra.kraId,
              actualValue: parseInt(actualScore) || 0,
              targetValue: kra.targetValue || 0.01,
              weight: kra.weight,
            };
          });
          return {
            objectiveId: objective.objectiveId,
            objectiveWeight: objective.objectiveWeight,
            krAs,
          };
        });

        const res = await onCalculate({
          employeeId: employeeData?.id || 0,
          referenceId: id,
          objectives,
        }).unwrap();
        if (res) {
          setTotalScore({
            colorType: res.colorType,
            totalAppraisalScore: res.totalAppraisalScore,
            performanceStatusLabelName: res.performanceStatusLabelName,
          });
        }
      } catch (err) {
        console.error("Error calculating total actual score:", err);
      }
    }, 500);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [watched, employeeData?.id, id, onCalculate, formMethods]);
  const [previewDoc, setPreviewDoc] = useState(null);
  useEffect(() => {
    if (item?.kraId && objIndex !== undefined && KraIndex !== undefined) {
      setScore({
        actualValue: parseFloat(currentActualValue) || 0,
        targetValue: item.targetValue,
        weight: item.weight,
      });
    }
  }, [item, KraIndex, objIndex, currentActualValue]);

  const allObjectives = useMemo(() => {
    return data?.responseObject?.objectives || [];
  }, [data?.responseObject?.objectives]);

  const handleGeneralDocumentsChange = (newDocuments) => {
    setGeneralDocuments(newDocuments);
  };
  const [form, setForm] = useState({});
  const submit = async (formData) => {
    try {
      // Validation
      if (!referenceId || referenceId === "0") {
        toast.error("Reference ID is required");
        return;
      }
      // if (
      //   form.RecommendationTitle === "Select Recommendation" ||
      //   !form.RecommendationTitle
      // ) {
      //   toast.error("Recommendation is required");
      //   return;
      // }
      if (!form?.summaryComment || !form?.summaryComment.trim()) {
        toast.error("Comment is required");
        return;
      }

      if (!employeeData?.id) {
        toast.error("Employee data is missing. Please refresh and try again.");
        return;
      }
      const binaryFormData = new FormData();

      binaryFormData?.append("KRAsJson", JSON.stringify(allKras));
      binaryFormData.append("ReferenceId", referenceId);
      binaryFormData.append("AppraisalId", id || "0");
      binaryFormData.append("AppraiseeId", (employeeData?.id || 20).toString());
      binaryFormData.append("AppraiserId", (user?.staffId || 0).toString());
      binaryFormData.append(
        "IsAcknowledged",
        formData?.IsAcknowledged ? "true" : "false"
      );
      binaryFormData.append(
        "IsRejected",
        formData?.IsRejected ? "true" : "false"
      );
      binaryFormData.append("RejectionComment", form?.RejectionComment || "");
      binaryFormData?.append("Comment", form.RejectionComment || "");
      binaryFormData?.append(
        "RecommendationId",
        form?.RecommendationId?.toString() || ""
      );

      const payload = {
        appraiserId: user?.staffId,
        referenceId: referenceId,
        summaryComment: form.summaryComment,
        isAcknowledged: false,
        isRejected: false,
        rejectionComment: form.RecommendationTitle,
      };
      const response = await onSubmit(payload).unwrap();
      toast.success(response?.statusMessage);
      navigation("/dashboard/employee/line-manager-appraisal");
    } catch (err) {
      console.log("Submission failed", err);
      toast.error(
        "Submission failed: " + (err.data?.statusMessage || err.message)
      );
    }
  };

  const [onSave] = useSaveLineManagerReviewMutation();
  const handleSave = async () => {
    try {
      let foundObjIndex = -1;
      let foundKraIndex = -1;
      data?.responseObject?.objectives?.forEach((objective, objIdx) => {
        objective.krAs?.forEach((kra, kraIdx) => {
          if (kra.kraId === kraId) {
            foundObjIndex = objIdx;
            foundKraIndex = kraIdx;
          }
        });
      });

      if (foundObjIndex === -1 || foundKraIndex === -1) {
        toast.error("KRA not found");
        return;
      }
      const kraKey = `${foundObjIndex}_${foundKraIndex}`;
      const kraActualScore =
        formMethods.getValues(`KRAs_${kraKey}_actualScore`) || 0;
      const comment = formMethods.getValues(`KRAs_${kraKey}_comments`) || "";
      const formData = new FormData();
      formData.append("ReferenceId", referenceId);
      formData.append("KRAId", kraId);
      const staffId = JSON.parse(localStorage.getItem("user"))?.staffId;
      formData.append("AppraiserId", staffId);
      formData.append("ActualScore", parseFloat(kraActualScore));
      formData.append("KRAScore", parseFloat(kraPerformanceScores[kraId] ?? 0));
      formData.append("Comment", comment);
      if (generalDocuments && generalDocuments.length > 0) {
        generalDocuments.forEach((artifact) => {
          formData.append(`Artifacts`, artifact.file);
        });
      }
      const res = await onSave(formData).unwrap();
      if (res.successful) {
        toast.success(res?.statusMessage || "KRA saved successfully");
        refetch();
      }
    } catch (err) {
      toast.error("Save failed: " + (err.data?.statusMessage || err.message));
    }
  };
  useEffect(() => {
    if (item?.employeeReviewDetails) {
      const defaultValues = item.employeeReviewDetails.find(
        (detail) => detail.appraisalType === 1
      );
      if (defaultValues?.kraActualScore) {
        reset({
          [`KRAs_${objIndex}_${KraIndex}_actualScore`]:
            defaultValues.kraActualScore,
          [`KRAs_${objIndex}_${KraIndex}_comments`]:
            defaultValues.comment || "",
        });
      }
    }
  }, [reset, objIndex, KraIndex, item]);
  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      return;
    }

    if (!allObjectives.length) return;
    if (debounceExpected.current) {
      clearTimeout(debounceExpected.current);
    }
    debounceExpected.current = setTimeout(async () => {
      try {
        const objectives = allObjectives.map((objective) => {
          const krAs = objective.krAs.map((kra) => {
            return {
              kraId: kra.kraId,
              actualValue: kra.targetValue || 0,
              targetValue: kra.targetValue || 0.01,
              weight: kra.weight,
            };
          });
          return {
            objectiveId: objective.objectiveId,
            objectiveWeight: objective.objectiveWeight,
            krAs,
          };
        });

        const res = await expectedScore({
          employeeId: employeeData?.id || 0,
          referenceId: id,
          objectives,
        }).unwrap();
        if (res) {
          setMaxScore(res.totalAppraisalScore);
        }
      } catch (err) {
        console.error("Error calculating total actual score:", err);
      }
    }, 500);

    return () => {
      if (debounceExpected.current) {
        clearTimeout(debounceExpected.current);
      }
    };
  }, [employeeData?.id, id, expectedScore, formMethods, allObjectives]);
  return (
    <PageLayout
      hasBack={true}
      backURL={"/dashboard/employee/line-manager-appraisal"}
    >
      <FormProvider {...formMethods}>
        <form id="selfAssessmentForm" onSubmit={handleSubmit(submit)}>
          <div className="flex items-center justify-between mb-4">
            <div className="w-[30px]" />
            <div>
              <p className="text-[#00AB09]">
                Total Score: {totalScore.totalAppraisalScore}/{maxScore} (
                {totalScore.totalAppraisalScore / maxScore})%
              </p>
            </div>
          </div>

          <Accordion
            aria-label={`Accordion 1`}
            selectionMode="single"
            titleWrapper="shadow-md rounded-md border border-gray-200"
          >
            <AccordionItem
              title={
                <div className="flex items-center gap-5 px-5">
                  <img src={person || "/placeholder.svg"} alt="" />{" "}
                  <span className="text-[#305F32] font-[600]">
                    EMPLOYEE PROFILE SUMMARY
                  </span>
                </div>
              }
              className="shadow-md rounded-md border border-gray-200 py-3 mb-2"
            >
              <MetaData data={employeeData} />
            </AccordionItem>
          </Accordion>

          {data?.responseObject?.objectives?.map((item, index) => (
            <Accordion
              aria-label={`Accordion ${index + 1}`}
              key={index + 1}
              selectionMode="single"
              titleWrapper="shadow-md rounded-md border border-gray-200"
            >
              <AccordionItem
                key={index + 1}
                title={
                  <div className="flex items-center justify-between px-5">
                    <div className="flex items-center gap-5">
                      <img src={flag || "/placeholder.svg"} alt="" />
                      <span className="text-[#4B664C] font-[600]">
                        OBJECTIVE: {item?.objectiveTitle}
                      </span>
                    </div>
                    <div>
                      <span className="text-[#4B664C] font-[600]">
                        Weight: {item?.objectiveWeight}%
                      </span>
                    </div>
                    <div className="w-10" />
                  </div>
                }
                className="shadow-md rounded-md border border-gray-200 py-3 mb-2"
              >
                <div>
                  {item.krAs.map((items, kraIndex) => (
                    <Accordion
                      key={kraIndex}
                      onClick={() => {
                        setKraId(items?.kraId);
                        setItem(items);
                        setKraIndex(kraIndex);
                        setObjIndex(index);
                      }}
                      aria-label={`Accordion ${kraIndex + 1}`}
                      selectionMode="single"
                      defaultExpandedKeys={["1"]}
                      titleWrapper="shadow-md rounded-md border border-gray-200"
                    >
                      <AccordionItem
                        key={kraIndex}
                        title={
                          <div className="flex items-center gap-5 px-5">
                            <img src={bull || "/placeholder.svg"} alt="" />
                            <span className="text-[#4B664C] font-[600]">
                              KRA: {items?.kraTitle}
                            </span>
                          </div>
                        }
                        className="shadow-md rounded-md border border-gray-200 py-3 mb-2"
                      >
                        <div>
                          {items.isAutoGenerated && (
                            <div className="flex items-center justify-between px-5">
                              <div />
                              <div>
                                <p className="text-[#4B664C] font-[600]">
                                  isAutogenerated
                                </p>
                              </div>
                            </div>
                          )}
                          <div
                            key={kraIndex + 1}
                            className="flex items-center gap-5 py-2 px-5"
                            style={{
                              display: "grid",
                              gridTemplateColumns: "180px 1fr",
                              alignItems: "center",
                              gap: "16px",
                            }}
                          >
                            <h2 className="text-[#305F32] font-[600]">
                              Title: {items.title}
                            </h2>
                            <h4 className="text-[#305F32] font-[500]">
                              Description: {items.description}
                            </h4>
                            <h2 className="text-[#305F32] font-[600]">
                              Target: {items.targetValue}
                            </h2>
                            <h4 className="text-[#00AB09] font-[500]">
                              Score:{" "}
                              {kraPerformanceScores[items.kraId] ||
                                items?.employeeReviewDetails?.filter(
                                  (item) => item.appraisalType === 1
                                )?.[0]?.kraScore}
                            </h4>
                            <h4 className="text-[#305F32] font-[500]">
                              Measurement Type: {items.measurementType}
                            </h4>
                            <h4 className="text-[#305F32] font-[500]">
                              Autogenerated:{" "}
                              {items.isAutoGenerated ? "Yes" : "No"}
                            </h4>
                            {items?.isAutoGenerated && (
                              <h4 className="text-[#305F32] font-[500]">
                                Source: {items.source}
                              </h4>
                            )}
                            <h4 className="text-[#305F32] font-[500]">
                              Period Start Date:{" "}
                              {FormatDateTime(
                                item.periodStartDate,
                                "DD/MM/YYYY"
                              )}
                            </h4>
                            <h4 className="text-[#305F32] font-[500]">
                              Period End Date:{" "}
                              {FormatDateTime(item.periodEndDate, "DD/MM/YYYY")}
                            </h4>
                            <h4 className="text-[#305F32] font-[500]">
                              Next Due Date:{" "}
                              {FormatDateTime(item.slaDueDate, "DD/MM/YYYY")}
                            </h4>
                            <h4 className="text-[#305F32] font-[500]">
                              SLA Due Date:{" "}
                              {FormatDateTime(item.slaDueDate, "DD/MM/YYYY")}
                            </h4>
                          </div>
                          <div className="w-full h-[1.5px] bg bg-gray-500 mx-4">
                            <h4 className="text-[#305F32] font-[500]">
                              Appraisee
                            </h4>
                          </div>
                          {items?.employeeReviewDetails
                            ?.filter((dat) => dat?.appraisaltype === 1)
                            ?.map((dat) => (
                              <>
                                {dat?.comment ? (
                                  <div className="bg-[#f9fbf7] rounded-xl border border-gray-200 p-6 w-full text-base">
                                    <AppraisalReviewDetails item={dat} />
                                  </div>
                                ) : null}
                              </>
                            ))}
                          {!items.isAutoGenerated && (
                            <div className="px-5 w-full overflow-hidden">
                              <FormInput
                                title={"KRA Actual Score"}
                                camelCase={`KRAs_${index}_${kraIndex}_actualScore`}
                                placeholder={"Enter actual score"}
                                className="mb-4"
                                type="number"
                                isRequired={true}
                              />
                              <FormTextArea
                                className="h-[100px]"
                                title={"KRA Comments (Optional)"}
                                camelCase={`KRAs_${index}_${kraIndex}_comments`}
                                placeholder="Enter comments for this KRA"
                                type="text"
                                textAreaClassName=""
                              />
                              <NeutralDocumentUploadComponent
                                name={`KRAs_${items?.kraId}_artifacts`}
                                title={"Upload General Supporting Documents"}
                                hasDocuments={generalDocuments?.length > 0}
                                onDocumentsChange={setGeneralDocuments}
                                id="generalSupportingDocuments"
                                defaultDocuments={
                                  items?.employeeReviewDetails?.filter(
                                    (item) => item.appraisalType === 1
                                  )?.[0]?.documents
                                }
                              />
                            </div>
                          )}
                          <div
                            style={{ marginTop: "1rem" }}
                            className={`flex justify-end mr-5`}
                          >
                            <ActionButtons
                              width={"auto"}
                              type={"button"}
                              onClick={handleSave}
                              className={DashboardStyle?.button_cage_weight}
                              bg="var(--primary-color)"
                            >
                              {isLoading ? "Saving..." : "Save"}
                            </ActionButtons>
                          </div>
                        </div>
                      </AccordionItem>
                    </Accordion>
                  ))}
                </div>
              </AccordionItem>
            </Accordion>
          ))}

          <div className="mt-8 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-lg font-semibold text-[#305F32] mb-4">
              Comments
            </h3>
            <div className="space-y-4">
              <FormTextArea
                moreRegister={{
                  onChange: (e) =>
                    setForm({ ...form, summaryComment: e.target.value }),
                }}
                className=" mb-3"
                title={"Comment"}
                name={"RejectionComment"}
                camelCase={"RejectionComment"}
                placeholder="Enter your overall performance comments"
                type="text"
                textAreaClassName=""
                isRequired={true}
              />

              {/* <FormSelect
                title={"Recommendations"}
                name={"RecommendationId"}
                camelCase={"RecommendationId"}
                placeholder={"select"}
                moreRegister={{
                  onChange: (e) =>
                    setForm({ ...form, RecommendationTitle: e.target.value }),
                }}
                array={recommendations?.result?.map((opt) => (
                  <option key={opt.id} value={opt.recommendationTitle}>
                    {opt.recommendationTitle}
                  </option>
                ))}
                defaultValue={"Select Recommendation"}
                isRequired={true}
              /> */}

              {/* <div className="flex gap-4 mt-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="IsRejected"
                    {...formMethods.register("IsRejected")}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="IsRejected"
                    className="text-sm font-medium text-gray-700"
                  >
                    Mark as Rejected
                  </label>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="IsAcknowledged"
                    defaultChecked={true}
                    {...formMethods.register("IsAcknowledged")}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="IsAcknowledged"
                    className="text-sm font-medium text-gray-700"
                  >
                    Mark as Acknowledged
                  </label>
                </div>
              </div> */}
            </div>
          </div>

          <div
            style={{ marginTop: "1rem" }}
            className={DashboardStyle.button_cage}
          >
            <ActionButtons
              width={"auto"}
              type={"button"}
              onClick={submit}
              className={DashboardStyle?.button_cage_weight}
              bg="var(--primary-color)"
              disabled={isLoading}
            >
              {isLoading ? "Submitting..." : "Submit"}
            </ActionButtons>
          </div>
        </form>
      </FormProvider>

      {previewDoc && (
        <Dialog
          open={Boolean(previewDoc)}
          onClose={() => setPreviewDoc(null)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>{previewDoc.fileName}</DialogTitle>
          <DialogContent>
            {(() => {
              const base64 =
                previewDoc?.base64Data ??
                previewDoc?.base64Content ??
                previewDoc?.base64; // support multiple possible names

              if (!previewDoc?.fileType || !base64) {
                return <p>Unsupported document format or missing file data.</p>;
              }

              const dataUrl = `data:${previewDoc.fileType};base64,${base64}`;

              if (previewDoc.fileType.includes("pdf")) {
                return (
                  <iframe
                    src={dataUrl}
                    width="100%"
                    height="600px"
                    title="PDF Preview"
                  />
                );
              }

              if (previewDoc.fileType.startsWith("image/")) {
                return (
                  <img
                    src={dataUrl}
                    alt={previewDoc.fileName}
                    style={{
                      width: "100%",
                      maxHeight: "600px",
                      objectFit: "contain",
                    }}
                  />
                );
              }

              if (
                previewDoc.fileType.includes("word") ||
                previewDoc.fileType.includes("officedocument") ||
                previewDoc.fileType.includes("excel") ||
                previewDoc.fileType.includes("spreadsheet") ||
                previewDoc.fileType.includes("presentation")
              ) {
                const handleDownload = () => {
                  const link = document.createElement("a");
                  link.href = dataUrl;
                  link.download = previewDoc.fileName;
                  link.click();
                };

                return (
                  <div style={{ textAlign: "center", padding: "20px" }}>
                    <p>
                      This document type (<strong>{previewDoc.fileType}</strong>
                      ) can’t be previewed directly.
                    </p>
                    <button
                      onClick={handleDownload}
                      style={{
                        backgroundColor: "#305F32",
                        color: "#fff",
                        padding: "10px 20px",
                        borderRadius: "6px",
                        cursor: "pointer",
                        border: "none",
                        marginTop: "10px",
                      }}
                    >
                      Download & Open File
                    </button>
                  </div>
                );
              }

              const handleDefaultDownload = () => {
                const link = document.createElement("a");
                link.href = dataUrl;
                link.download = previewDoc.fileName;
                link.click();
              };

              return (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  <p>Preview not supported for this file type.</p>
                  <button
                    onClick={handleDefaultDownload}
                    style={{
                      backgroundColor: "#305F32",
                      color: "#fff",
                      padding: "10px 20px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      border: "none",
                      marginTop: "10px",
                    }}
                  >
                    Download File
                  </button>
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>
      )}
    </PageLayout>
  );
};

export default LineManagerView;
